import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReplaySubject, Subject, BehaviorSubject } from 'rxjs';
import {
  CustomerNotesMetadata,
  DeclineCancellationEvent,
  DeclinedOrderMetadata,
  HistoryMetadata,
  NotesMetadata,
  Order,
  Origin,
  RequestCancellationMetadata,
  SalesOrdersService,
  Status,
  UpdateContractDurationMetadata,
  UpdatedRequestedActivationMetadata,
  UpdatedTagsMetadata,
  UserInterface,
} from '@vendasta/sales-orders';
import { ContractDurationEnumToKeys, OrderDetailField, StatusToBadgeMapping } from '@vendasta/sales-ui';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { HistoryService } from './history.service';
import { SubmitterInfo } from './history.interface';

@Component({
  selector: 'orders-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
  providers: [HistoryService],
  standalone: false,
})
export class HistoryComponent implements OnInit, OnDestroy {
  @Input() order: Order;

  orderUsers$$ = new BehaviorSubject<{ [key: string]: UserInterface }>({});
  orderUsers$ = this.orderUsers$$.asObservable();
  orderDetailFields$$ = new ReplaySubject<OrderDetailField[]>(1);
  orderDetailFields$ = this.orderDetailFields$$.asObservable();
  orderCreatedByInfo$$ = new ReplaySubject<SubmitterInfo>(1);
  orderCreatedByInfo$ = this.orderCreatedByInfo$$.asObservable();

  openExpansionPanel$$ = new BehaviorSubject<boolean>(false);
  openExpansionPanel$ = this.openExpansionPanel$$.asObservable();

  private ngUnsubscribe = new Subject<void>();

  constructor(
    private readonly salesOrdersService: SalesOrdersService,
    private readonly translateService: TranslateService,
    private readonly historyService: HistoryService,
  ) {}

  ngOnInit(): void {
    this.salesOrdersService
      .getUsers(this.order.orderId, this.order.businessId)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((users) => {
        this.orderUsers$$.next(users);
        const orderDetails = OrderDetailField.createFromOrderWithUsers(this.order, users);
        this.orderDetailFields$$.next(orderDetails);
        let createdByName = '';
        if (!!orderDetails && orderDetails.length > 0) {
          createdByName = this.getDisplayNameForUser(orderDetails[0].user);
        }
        this.orderCreatedByInfo$$.next(this.getOrderSubmitterBadgeInfo(createdByName));
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  buildDetailName(orderDetailField: OrderDetailField, isChild: boolean): string {
    if (isChild) {
      return this.historyService.getEventNameTranslationKeyForOrderDetailField(orderDetailField);
    }
    // Not all sales histories were created with a status
    return StatusToBadgeMapping.get(orderDetailField.status)?.text || StatusToBadgeMapping.get(Status.SUBMITTED).text;
  }

  buildDetailValue(orderDetailField: OrderDetailField): string {
    const nameAndDateString = this.formatDateAndNamePair(orderDetailField.date, orderDetailField.user);
    return this.buildValueFromMetadata(nameAndDateString, orderDetailField.metadata);
  }

  private formatDateAndNamePair(date: Date, user: UserInterface): string {
    const displayName = this.historyService.getDisplayNameForSalesUser(user);
    const formattedDate = this.historyService.formatDateWithTime(date);
    if (displayName) {
      return this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.DATE_BY_USER', {
        date: formattedDate,
        user: displayName,
      });
    }
    // If there's no user just display the date ()
    return formattedDate;
  }

  private buildValueFromMetadata(nameAndDateString: string, metadata: HistoryMetadata): string {
    if (
      metadata instanceof RequestCancellationMetadata ||
      metadata instanceof NotesMetadata ||
      metadata instanceof CustomerNotesMetadata
    ) {
      let notes = metadata.current;
      if (metadata.event.id === DeclineCancellationEvent) {
        notes = metadata.event.name + '\n' + metadata.current;
      }
      return nameAndDateString + '\n\n' + notes;
    }

    if (metadata instanceof DeclinedOrderMetadata) {
      const notes = [...metadata.declinedReasons, metadata.current].join('\n');
      return nameAndDateString + '\n\n' + notes;
    }

    if (metadata instanceof UpdatedTagsMetadata) {
      const tagsString =
        !!metadata.tags && metadata.tags.length > 0
          ? this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
              value: metadata.tags.join(', '),
            })
          : this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.ALL_TAGS_REMOVED');
      return nameAndDateString + '\n\n' + tagsString;
    }

    if (metadata instanceof UpdatedRequestedActivationMetadata) {
      if (!!metadata.previous && !!metadata.current) {
        return (
          nameAndDateString +
          '\n\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: this.historyService.formatDate(metadata.previous),
            toValue: this.historyService.formatDate(metadata.current),
          })
        );
      }

      if (metadata.current) {
        return (
          nameAndDateString +
          '\n\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
            value: this.historyService.formatDate(metadata.current),
          })
        );
      }

      if (metadata.previous) {
        return (
          nameAndDateString +
          '\n\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: this.historyService.formatDate(metadata.previous),
            toValue: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_START_DATE'),
          })
        );
      }
      return (
        nameAndDateString +
        '\n\n' +
        this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
          value: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_START_DATE'),
        })
      );
    }

    if (metadata instanceof UpdateContractDurationMetadata) {
      if (!!metadata.previous && !!metadata.current) {
        const previousDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.previous.duration],
        );
        const previousDuration = `${metadata.previous.value} ${previousDurationPeriod}`;
        const currentDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.current.duration],
        );
        const currentDuration = `${metadata.current.value} ${currentDurationPeriod}`;
        return (
          nameAndDateString +
          '\n\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: previousDuration,
            toValue: currentDuration,
          })
        );
      }
      if (metadata.current) {
        const currentDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.current.duration],
        );
        const currentDuration = `${metadata.current.value} ${currentDurationPeriod}`;
        return (
          nameAndDateString +
          '\n\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
            value: currentDuration,
          })
        );
      }
      if (metadata.previous) {
        const previousDurationPeriod = this.translateService.instant(
          ContractDurationEnumToKeys[metadata.previous.duration],
        );
        const previousDuration = `${metadata.previous.value} ${previousDurationPeriod}`;
        return (
          nameAndDateString +
          '\n\n' +
          this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_FROM_TO', {
            fromValue: previousDuration,
            toValue: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_LENGTH'),
          })
        );
      }
      return (
        nameAndDateString +
        '\n\n' +
        this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.UPDATED_TO', {
          value: this.translateService.instant('FRONTEND.SALES_UI.ORDER_DETAILS.NO_CONTRACT_LENGTH'),
        })
      );
    }
    return nameAndDateString;
  }

  private getDisplayNameForUser(user: UserInterface): string {
    if (!user) {
      return '';
    }
    const names = [];
    if (user.firstName) {
      names.push(user.firstName);
    }
    if (user.lastName) {
      names.push(user.lastName);
    }
    const displayName = names.join(' ');
    if (!displayName) {
      return user.email;
    }
    return displayName;
  }

  getOrderSubmitterBadgeInfo(name: string): SubmitterInfo {
    switch (this.order.origin || Origin.SSC) {
      case Origin.SSC:
        return { name: name, color: 'purple', translationKey: 'FRONTEND.SALES_UI.SALESPERSON' };
      case Origin.VBC:
        return { name: name, color: 'green', translationKey: 'FRONTEND.SALES_UI.CLIENT' };
      case Origin.PC:
        return { name: name, color: 'blue', translationKey: 'FRONTEND.SALES_UI.ADMIN' };
      default:
        return null;
    }
  }

  // called by parent components for print-friendly display
  public openDropdowns(): void {
    this.openExpansionPanel$$.next(true);
  }
}
